import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, Save, FileText, ArrowLeft, Eye, Link2 as LinkIcon } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TYPES = [["text", "Short text"], ["textarea", "Long text"], ["select", "Multiple choice"], ["checkboxes", "Checkboxes"], ["yes_no", "Yes / No"], ["scale", "Scale 1-5"]];
const ATTACH = [["template", "Reusable template"], ["booking_flow", "Booking flow (all my bookings)"], ["service", "Specific offering"], ["event", "Specific event"]];

const emptyForm = { title: "", description: "", attach_type: "template", attach_id: "", attach_title: "", questions: [], active: true };

export const FormBuilderPanel = () => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["my-forms"], queryFn: () => api.get("/forms/mine").then((r) => r.data) });
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: () => api.get("/services").then((r) => r.data) });
  const { data: events = [] } = useQuery({ queryKey: ["events"], queryFn: () => api.get("/events").then((r) => r.data) });

  const refresh = () => qc.invalidateQueries({ queryKey: ["my-forms"] });
  const del = async (id) => {
    if (!window.confirm("Delete this form? Responses stay on member profiles.")) return;
    try { await api.delete(`/forms/${id}`); toast.success("Form deleted"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (viewing) return <FormResponses formId={viewing} onBack={() => setViewing(null)} />;
  if (editing) return <FormEditor form={editing} services={services} events={events} onDone={() => { setEditing(null); refresh(); }} onCancel={() => setEditing(null)} />;

  return (
    <div data-testid="form-builder-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><FileText size={20} className="text-[#144F75]" /> Forms & questionnaires</h2>
          <p className="text-sm text-[#4A5D6B]">Build custom forms — attach them to an offering, event, or your booking flow, or keep them as reusable templates. Responses save to member profiles.</p>
        </div>
        <button onClick={() => setEditing({ ...emptyForm })} data-testid="form-new" className="eyw-btn !py-2 text-sm"><Plus size={15} /> New form</button>
      </div>
      {isLoading ? <p className="text-sm text-[#4A5D6B] flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</p> : (
        <div className="space-y-3">
          {(data?.forms || []).map((f) => (
            <div key={f.id} className="eyw-card p-5 flex flex-wrap items-center gap-3" data-testid={`form-row-${f.id}`}>
              <div className="flex-1 min-w-[220px]">
                <p className="text-[#1A242B] font-medium">{f.title} {!f.active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#E5E0D8] text-[#4A5D6B] ml-1">inactive</span>}</p>
                <p className="text-xs text-[#8FA89B]">{ATTACH.find(([v]) => v === f.attach_type)?.[1]}{f.attach_title ? ` · ${f.attach_title}` : ""} · {f.questions.length} question{f.questions.length !== 1 ? "s" : ""} · {f.response_count} response{f.response_count !== 1 ? "s" : ""}</p>
              </div>
              <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/f/${f.share_token}`); toast.success("Share link copied — send it by email or text."); }} data-testid={`form-link-${f.id}`} className="text-xs text-[#5C8A72] font-medium hover:underline flex items-center gap-1"><LinkIcon size={13} /> Copy link</button>
              <button onClick={() => setViewing(f.id)} data-testid={`form-responses-${f.id}`} className="text-xs text-[#144F75] font-medium hover:underline flex items-center gap-1"><Eye size={13} /> Responses</button>
              <button onClick={() => setEditing(f)} data-testid={`form-edit-${f.id}`} className="text-xs text-[#144F75] font-medium hover:underline">Edit</button>
              <button onClick={() => del(f.id)} data-testid={`form-delete-${f.id}`} className="text-red-400 hover:text-red-600"><Trash2 size={15} /></button>
            </div>
          ))}
          {(data?.forms || []).length === 0 && <p className="text-sm text-[#8FA89B] py-8 text-center">No custom forms yet — create your first questionnaire.</p>}
        </div>
      )}
      <p className="text-xs text-[#8FA89B] mt-4">Your booking intake form lives in the "Intake Form" tab — these custom forms extend it.</p>
    </div>
  );
};

function FormEditor({ form, services, events, onDone, onCancel }) {
  const [f, setF] = useState({ ...form });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const updQ = (i, patch) => set("questions", f.questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f, questions: f.questions.map((q) => ({ ...q, options: Array.isArray(q.options) ? q.options : String(q.options || "").split(",").map((s) => s.trim()).filter(Boolean) })) };
      if (f.id) await api.put(`/forms/${f.id}`, body);
      else await api.post("/forms", body);
      toast.success("Form saved");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const attachOptions = f.attach_type === "service" ? services.map((s) => [s.id, s.title]) : f.attach_type === "event" ? events.map((e) => [e.id, `${e.title} (${e.date})`]) : [];

  return (
    <div data-testid="form-editor">
      <button onClick={onCancel} className="text-sm text-[#4A5D6B] hover:text-[#144F75] flex items-center gap-1 mb-4"><ArrowLeft size={15} /> Back to forms</button>
      <div className="eyw-card p-6 space-y-3">
        <input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Form title — e.g. Pre-retreat questionnaire" data-testid="form-title" className="eyw-input w-full" />
        <textarea rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Short description members see (optional)" data-testid="form-description" className="eyw-input w-full resize-none" />
        <div className="flex flex-wrap gap-3 items-center">
          <select value={f.attach_type} onChange={(e) => set("attach_type", e.target.value)} data-testid="form-attach-type" className="eyw-input text-sm">
            {ATTACH.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {attachOptions.length > 0 && (
            <select value={f.attach_id} onChange={(e) => { const o = attachOptions.find(([id]) => id === e.target.value); set("attach_id", e.target.value); setF((x) => ({ ...x, attach_id: e.target.value, attach_title: o?.[1] || "" })); }} data-testid="form-attach-id" className="eyw-input text-sm min-w-[220px]">
              <option value="">— choose —</option>
              {attachOptions.map(([id, l]) => <option key={id} value={id}>{l}</option>)}
            </select>
          )}
          <label className="flex items-center gap-1.5 text-sm text-[#4A5D6B] cursor-pointer"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} className="accent-[#144F75]" data-testid="form-active" /> Active</label>
        </div>
      </div>
      <div className="space-y-3 mt-4">
        {f.questions.map((q, i) => (
          <div key={i} className="eyw-card p-4" data-testid={`fb-q-${i}`}>
            <div className="flex flex-wrap gap-3 items-start">
              <input value={q.label} onChange={(e) => updQ(i, { label: e.target.value })} placeholder={`Question ${i + 1}`} data-testid={`fb-q-label-${i}`} className="eyw-input flex-1 min-w-[220px]" />
              <select value={q.type} onChange={(e) => updQ(i, { type: e.target.value })} data-testid={`fb-q-type-${i}`} className="eyw-input text-sm">
                {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <label className="flex items-center gap-1.5 text-xs text-[#4A5D6B] pt-3 cursor-pointer"><input type="checkbox" checked={q.required} onChange={(e) => updQ(i, { required: e.target.checked })} /> Required</label>
              <button onClick={() => set("questions", f.questions.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600 pt-2.5" data-testid={`fb-q-remove-${i}`}><Trash2 size={16} /></button>
            </div>
            {["select", "checkboxes"].includes(q.type) && (
              <input value={Array.isArray(q.options) ? q.options.join(", ") : q.options || ""} onChange={(e) => updQ(i, { options: e.target.value.split(",").map((s) => s.trimStart()) })} placeholder="Choices, separated by commas" data-testid={`fb-q-options-${i}`} className="eyw-input w-full mt-2 text-sm" />
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button onClick={() => set("questions", [...f.questions, { id: "", label: "", type: "text", options: [], required: false }])} data-testid="fb-add-question" className="eyw-btn-ghost !py-2 text-sm"><Plus size={15} /> Add question</button>
        <button onClick={save} disabled={saving} data-testid="fb-save" className="eyw-btn !py-2 text-sm disabled:opacity-60">{saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Save form</button>
      </div>
    </div>
  );
}

function FormResponses({ formId, onBack }) {
  const { data, isLoading } = useQuery({ queryKey: ["form-responses", formId], queryFn: () => api.get(`/forms/${formId}/responses`).then((r) => r.data) });
  return (
    <div data-testid="form-responses">
      <button onClick={onBack} className="text-sm text-[#4A5D6B] hover:text-[#144F75] flex items-center gap-1 mb-4"><ArrowLeft size={15} /> Back to forms</button>
      {isLoading ? <p className="text-sm text-[#4A5D6B] flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</p> : (
        <>
          <h2 className="font-serif text-2xl text-[#1A242B] mb-1">{data.form.title}</h2>
          <p className="text-sm text-[#8FA89B] mb-5">{data.responses.length} response{data.responses.length !== 1 ? "s" : ""}</p>
          <div className="space-y-4">
            {data.responses.map((r) => (
              <div key={r.id} className="eyw-card p-5" data-testid={`fr-response-${r.id}`}>
                <p className="text-xs text-[#8FA89B] mb-2">{r.user_name || "Member"} · {String(r.created_at).slice(0, 10)}{r.context ? ` · ${r.context}` : ""}</p>
                {r.answers.map((a, i) => (
                  <div key={i} className="mb-1.5"><p className="text-xs font-medium text-[#4A5D6B]">{a.label}</p><p className="text-sm text-[#1A242B]">{a.answer || "—"}</p></div>
                ))}
              </div>
            ))}
            {data.responses.length === 0 && <p className="text-sm text-[#8FA89B] py-8 text-center">No responses yet.</p>}
          </div>
        </>
      )}
    </div>
  );
}

export default FormBuilderPanel;
